import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, ChevronLeft, ChevronRight, Save, Lock, Unlock, Check, Target, TrendingUp, DollarSign, MousePointerClick, Eye, ShoppingCart, Percent } from 'lucide-react';

// Platform catalog (order + visual)
const PLATFORMS = [
  {
    key: 'GOOGLE_ADS',
    label: 'Google Ads',
    icon: '/google_ads-512.webp',
    connectionPlatform: 'GOOGLE_ADS',
    color: 'from-red-500 to-orange-500',
    borderColor: 'border-red-100',
    bgColor: 'bg-red-50'
  },
  {
    key: 'META_ADS',
    label: 'Meta Ads',
    icon: '/6033716.png',
    connectionPlatform: 'META_ADS',
    color: 'from-blue-500 to-indigo-500',
    borderColor: 'border-blue-100',
    bgColor: 'bg-blue-50'
  },
  {
    key: 'TIKTOK_ADS',
    label: 'TikTok Ads',
    icon: '/tiktokv4.svg',
    connectionPlatform: 'TIKTOK_ADS',
    color: 'from-cyan-500 to-pink-500',
    borderColor: 'border-cyan-100',
    bgColor: 'bg-cyan-50'
  },
  {
    key: 'CRITEO',
    label: 'Criteo',
    icon: '/CRcriteopng.png',
    connectionPlatform: 'CRITEO_ADS',
    color: 'from-orange-500 to-amber-500',
    borderColor: 'border-orange-100',
    bgColor: 'bg-orange-50'
  },
  {
    key: 'RTB_HOUSE',
    label: 'RTB House',
    icon: '/RTBhouseiconPNG.png',
    connectionPlatform: 'RTB_HOUSE',
    color: 'from-teal-500 to-emerald-500',
    borderColor: 'border-teal-100',
    bgColor: 'bg-teal-50'
  },
  {
    key: 'PINTEREST',
    label: 'Pinterest',
    icon: '/pinterestlogopng.png',
    connectionPlatform: 'PINTEREST_ADS',
    color: 'from-pink-500 to-red-500',
    borderColor: 'border-pink-100',
    bgColor: 'bg-pink-50'
  }
];

// TOTAL (blended) always visible as a header card
const TOTAL_CARD = {
  key: 'TOTAL',
  label: 'Total (Blended)',
  color: 'from-slate-700 to-slate-900',
  borderColor: 'border-slate-200',
  bgColor: 'bg-slate-50'
};

const METRICS = [
  { key: 'spend_target',       label: 'Spend Budget',   icon: DollarSign,        prefix: '₺',  type: 'currency' },
  { key: 'revenue_target',     label: 'Revenue Target', icon: TrendingUp,        prefix: '₺',  type: 'currency' },
  { key: 'sessions_target',    label: 'Sessions',       icon: Eye,               prefix: '',   type: 'integer' },
  { key: 'clicks_target',      label: 'Clicks',         icon: MousePointerClick, prefix: '',   type: 'integer' },
  { key: 'conversions_target', label: 'Conversions',    icon: ShoppingCart,      prefix: '',   type: 'integer' },
  { key: 'roas_target',        label: 'ROAS',           icon: Target,            prefix: '',   type: 'decimal', suffix: 'x' },
  { key: 'cpa_target',         label: 'CPA',            icon: Percent,           prefix: '₺',  type: 'currency' }
];

function fmtInput(v) {
  if (v === null || v === undefined || v === '') return '';
  return String(v);
}

function parseValue(v) {
  if (v === '' || v === null || v === undefined) return null;
  const cleaned = String(v).replace(/[^0-9.,-]/g, '').replace(/,/g, '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

function fmtPreview(v, type, prefix, suffix) {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  const p = prefix || '';
  const s = suffix || '';
  if (type === 'currency') return `${p}${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}${s}`;
  if (type === 'decimal') return `${p}${n.toFixed(2)}${s}`;
  return `${p}${n.toLocaleString('tr-TR')}${s}`;
}

export default function MonthlyTargetsDashboard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [targets, setTargets] = useState([]);
  const [connections, setConnections] = useState([]);
  const [edits, setEdits] = useState({}); // { [channel_key]: { [metric]: value } }
  const [saving, setSaving] = useState({});

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : '';
  const monthName = new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const connectedPlatformKeys = useMemo(() => {
    const activeSet = new Set(
      connections
        .filter(c => String(c.status || '').toUpperCase() === 'ACTIVE')
        .map(c => String(c.platform || '').toUpperCase())
    );
    return PLATFORMS.filter(p => activeSet.has(p.connectionPlatform));
  }, [connections]);

  const byChannel = useMemo(() => {
    const m = {};
    for (const t of targets) {
      const key = t.channel_key;
      if (!m[key] || (!t.account_id && m[key].account_id)) m[key] = t;
    }
    return m;
  }, [targets]);

  const fetchAll = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const [tRes, cRes] = await Promise.all([
        fetch(`https://api.glowytics.com/api/monthly-targets?year=${year}&month=${month}`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId }
        }),
        fetch('https://api.glowytics.com/api/auth/connections', {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId }
        })
      ]);
      if (tRes.ok) {
        const data = await tRes.json();
        setTargets(Array.isArray(data) ? data : []);
      }
      if (cRes.ok) {
        const data = await cRes.json();
        setConnections(Array.isArray(data) ? data : []);
      }
      setEdits({});
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [brandId, year, month]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const changeMonth = (delta) => {
    let y = year;
    let m = month + delta;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };

  const getValue = (channel_key, metric) => {
    const editVal = edits[channel_key]?.[metric];
    if (editVal !== undefined) return editVal;
    const row = byChannel[channel_key];
    return row && row[metric] !== null && row[metric] !== undefined ? String(row[metric]) : '';
  };

  const setEdit = (channel_key, metric, val) => {
    setEdits(prev => ({
      ...prev,
      [channel_key]: { ...(prev[channel_key] || {}), [metric]: val }
    }));
  };

  const hasEdits = (channel_key) => edits[channel_key] && Object.keys(edits[channel_key]).length > 0;

  const saveCard = async (channel_key) => {
    if (!brandId || !hasEdits(channel_key)) return;
    setSaving(prev => ({ ...prev, [channel_key]: true }));
    try {
      const row = byChannel[channel_key] || {};
      const payload = {
        year,
        month,
        channel_key,
        account_id: row.account_id || null,
        revenue_source: row.revenue_source || 'ga4'
      };
      for (const m of METRICS) {
        if (edits[channel_key] && m.key in edits[channel_key]) {
          payload[m.key] = parseValue(edits[channel_key][m.key]);
        }
      }
      const res = await fetch('https://api.glowytics.com/api/monthly-targets', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      toast.success(`${channel_key === 'TOTAL' ? 'Total' : PLATFORMS.find(p => p.key === channel_key)?.label} targets saved`);
      setEdits(prev => { const n = { ...prev }; delete n[channel_key]; return n; });
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(prev => ({ ...prev, [channel_key]: false }));
    }
  };

  const toggleLock = async (channel_key) => {
    const row = byChannel[channel_key];
    if (!row?.id) return;
    const action = row.is_locked ? 'unlock' : 'lock';
    try {
      const res = await fetch(`https://api.glowytics.com/api/monthly-targets/${row.id}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!res.ok) throw new Error('Lock toggle failed');
      toast.success(row.is_locked ? 'Unlocked' : 'Locked');
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!brandId) {
    return (
      <div className="p-8">
        <div className="max-w-2xl mx-auto p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Please select a brand first.
        </div>
      </div>
    );
  }

  const renderCard = (platformCfg) => {
    const { key, label, icon, color, borderColor, bgColor } = platformCfg;
    const row = byChannel[key];
    const isLocked = row?.is_locked;
    const isSaving = !!saving[key];
    const dirty = hasEdits(key);
    const isTotal = key === 'TOTAL';

    return (
      <div
        key={key}
        className={`bg-white rounded-2xl border ${borderColor} shadow-sm overflow-hidden ${isTotal ? 'ring-2 ring-slate-200' : ''}`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 ${bgColor} border-b ${borderColor}`}>
          <div className="flex items-center gap-3">
            {isTotal ? (
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-md`}>
                <Target className="w-6 h-6 text-white" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm border border-gray-100">
                <img src={icon} alt={label} className="w-8 h-8 object-contain" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-900">{label}</h3>
              {row?.id ? (
                <p className="text-[11px] text-gray-500">
                  Last updated: {row.updated_at ? new Date(row.updated_at).toLocaleDateString('tr-TR') : '—'}
                  {isLocked && <span className="ml-1.5 inline-flex items-center gap-0.5 text-amber-600"><Lock className="w-2.5 h-2.5" /> locked</span>}
                </p>
              ) : (
                <p className="text-[11px] text-gray-400">No targets set</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {row?.id && (
              <button
                onClick={() => toggleLock(key)}
                className={`p-2 rounded-lg transition-colors ${isLocked ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                title={isLocked ? 'Unlock' : 'Lock'}
              >
                {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
              </button>
            )}
            <button
              onClick={() => saveCard(key)}
              disabled={!dirty || isSaving || isLocked}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                dirty && !isLocked
                  ? `bg-gradient-to-r ${color} text-white hover:opacity-90 shadow-sm`
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>

        {/* Fields */}
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {METRICS.map(m => {
              const MIcon = m.icon;
              const val = getValue(key, m.key);
              const isDirty = edits[key] && m.key in edits[key];
              const preview = fmtPreview(val, m.type, m.prefix, m.suffix);
              return (
                <div key={m.key}>
                  <label className="flex items-center gap-1.5 text-[11px] font-medium text-gray-600 uppercase tracking-wide mb-1">
                    <MIcon className="w-3 h-3 text-gray-400" />
                    {m.label}
                  </label>
                  <div className="relative">
                    {m.prefix && (
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                        {m.prefix}
                      </span>
                    )}
                    <input
                      type="text"
                      value={fmtInput(val)}
                      onChange={(e) => setEdit(key, m.key, e.target.value)}
                      disabled={isLocked}
                      placeholder="—"
                      className={`w-full ${m.prefix ? 'pl-6' : 'pl-3'} ${m.suffix ? 'pr-6' : 'pr-3'} py-2 text-sm rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                        isLocked
                          ? 'bg-gray-50 border border-gray-200 text-gray-400 cursor-not-allowed'
                          : isDirty
                          ? 'bg-amber-50 border border-amber-300 focus:ring-amber-200 focus:border-amber-400'
                          : 'bg-white border border-gray-200 focus:ring-blue-200 focus:border-blue-400'
                      }`}
                    />
                    {m.suffix && (
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                        {m.suffix}
                      </span>
                    )}
                  </div>
                  {preview && preview !== fmtInput(val) && (
                    <p className="text-[10px] text-gray-400 mt-0.5 ml-0.5">{preview}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Monthly Targets</h1>
            <p className="text-sm text-gray-500 mt-1">
              Set monthly budgets and revenue goals for each connected platform.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => changeMonth(-1)}
              disabled={loading}
              className="p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-5 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm min-w-[180px] text-center">
              <div className="text-[11px] text-gray-500 uppercase tracking-wide">Period</div>
              <div className="text-base font-semibold text-gray-900">{monthName}</div>
            </div>
            <button
              onClick={() => changeMonth(1)}
              disabled={loading}
              className="p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); }}
              className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm text-sm text-gray-600"
            >
              Today
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
            <p className="text-sm">Loading targets…</p>
          </div>
        ) : (
          <>
            {/* TOTAL card */}
            {renderCard(TOTAL_CARD)}

            {/* Connected platform cards */}
            {connectedPlatformKeys.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
                <Target className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-base font-medium text-gray-700 mb-1">No connected ad platforms</h3>
                <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
                  Connect ad platforms (Google Ads, Meta Ads, TikTok, etc.) under Settings to start setting per-platform targets.
                </p>
                <a
                  href="/settings?section=api-connections"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                >
                  Go to Platform Connections
                </a>
              </div>
            ) : (
              <div className="space-y-4">
                {connectedPlatformKeys.map(renderCard)}
              </div>
            )}

            {/* Info footer */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-xs text-blue-800 leading-relaxed">
                <p className="font-medium mb-0.5">Tips</p>
                <ul className="space-y-0.5 list-disc list-inside text-blue-700/90">
                  <li>Leave fields empty to skip that metric — targets are optional per metric.</li>
                  <li>Amber border = unsaved change. Click <strong>Save</strong> per card to persist.</li>
                  <li>Use <strong>Lock</strong> after the month closes to prevent accidental edits.</li>
                  <li>Only platforms connected to this brand are shown below <strong>TOTAL</strong>.</li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
